// SignalRContext.tsx
import { SOCKET_RECONNECT_NOW_EVENT } from 'platform/appLifecycle';
import React, { createContext, useEffect, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useAuth } from '@clerk/clerk-react';
import { applyServerContext, isDevViteProxyEnabled } from 'utils/axios';
import { getClerkToken } from 'utils/auth/token';
import { useSnackbar } from 'notistack';
import { dispatch } from 'store';
import { setChatUsers, setChatUsersRequested, setChatUsersSavedMessages } from 'store/slices/chat';
import { getDataOnLoad } from 'services/dataLoader';
import {
  noteBrowserEvent,
  registerSocketHealthProbe,
  registerSocketReconnector,
  socketConnectionHealth,
} from 'utils/socket-health-monitor';

function getSignalRHubUrl(): string {
  if (isDevViteProxyEnabled()) {
    return '/hub/signalr';
  }
  const base = import.meta.env.VITE_APP_SOCKET_API_URL || 'https://app.funner.co.il';
  return `${applyServerContext(base)}/hub/signalr`;
}

/** Escalating reconnect delays, capped at 60s — retried forever (never give up). */
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 30000, 60000];

/** ±20% jitter so all clients don't stampede the server at once when it comes back up. */
const jitterDelay = (ms: number) => Math.round(ms * (0.8 + Math.random() * 0.4));

const retryDelayFor = (attempt: number) =>
  jitterDelay(RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]);

/**
 * Fired on window after a fully-closed connection is revived (Path B).
 * SignalR's onreconnected does NOT fire for manual restarts, so views that
 * re-sync on reconnect must listen to this event as well.
 */
export const SOCKET_RECONNECTED_EVENT = 'funner:socket-reconnected';

interface SignalRContextType {
  connection: HubConnection | null;
}

const SignalRContext = createContext<SignalRContextType>({ connection: null });

const SignalRProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [connection, setConnection] = useState<HubConnection | null>(null);

  const isConnectionRef = React.useRef<boolean>(false);

  //* Socket connection - created once per sign-in, NOT recreated on token refresh.
  //* accessTokenFactory handles getting fresh tokens for reconnects.
  //* Resilience: infinite automatic reconnect with jitter (Path A), plus an onclose
  //* safety net + online-event nudge that revive a fully-closed connection (Path B).
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let disposed = false;
    let conn: HubConnection | null = null;
    let startLoopRunning = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let wakeRetry: (() => void) | null = null;
    let probeCleanup: (() => void) | null = null;
    let reconnectorCleanup: (() => void) | null = null;

    const waitBeforeRetry = (ms: number) =>
      new Promise<void>((resolve) => {
        wakeRetry = resolve;
        retryTimer = setTimeout(resolve, ms);
      });

    //* Start (or revive) the connection, retrying forever until success or teardown.
    const startWithRetry = async (isRecovery: boolean) => {
      if (!conn || startLoopRunning) return;
      startLoopRunning = true;
      for (let attempt = 0; !disposed; attempt++) {
        try {
          socketConnectionHealth.startAttempt(attempt, isRecovery);
          await conn.start();
          if (disposed) break;
          isConnectionRef.current = true;
          setConnection(conn);
          socketConnectionHealth.connected(conn.connectionId ?? null, isRecovery);
          if (isRecovery) {
            //* Same re-sync as onreconnected — the disconnect window may hold missed events.
            getDataOnLoad();
            window.dispatchEvent(new CustomEvent(SOCKET_RECONNECTED_EVENT));
          }
          break;
        } catch (err) {
          console.error('SignalR start failed, retrying: ', err);
          socketConnectionHealth.startFailed(attempt, err);
          await waitBeforeRetry(retryDelayFor(attempt + 1));
        }
      }
      startLoopRunning = false;
    };

    const init = async () => {
      //* Wait for Clerk to settle (best effort) but never bail — accessTokenFactory
      //* fetches a fresh token per negotiate, and the start loop retries on 401.
      await getClerkToken().catch(() => null);
      if (disposed) return;

      conn = new HubConnectionBuilder()
        .withUrl(getSignalRHubUrl(), {
          accessTokenFactory: () => getClerkToken().then((t) => t || ''),
        })
        //* Never returns null → the automatic reconnect never gives up (Path A).
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (ctx) => retryDelayFor(ctx.previousRetryCount),
        })
        .build();

      //* Safety net (Path B): if the connection fully closes anyway, revive it —
      //* unless this is an intentional teardown (sign-out / unmount).
      conn.onclose((error) => {
        isConnectionRef.current = false;
        socketConnectionHealth.closed(error, disposed);
        if (disposed) return;
        console.warn('SignalR closed unexpectedly — reviving', error);
        void startWithRetry(true);
      });

      //* ניטור בלבד — בדיקת מצב חי עבור טאב "ניטור סוקט" וה-watchdog.
      probeCleanup = registerSocketHealthProbe(() => ({
        state: conn ? String(conn.state) : 'NoConnection',
        connectionId: conn?.connectionId ?? null,
      }));

      //* ריפוי זומבי (14.10): ה-watchdog מזהה "מחובר אבל שקט" (נצפה בפרודקשן —
      //* עד 15.5 שעות שקט ברצף) ומבקש reconnect יזום: stop → onclose מחייה
      //* דרך startWithRetry(true), כולל resync של הנתונים שהוחמצו.
      reconnectorCleanup = registerSocketReconnector(() => {
        if (disposed || !conn) return;
        if (conn.state === HubConnectionState.Connected) {
          void conn.stop();
        } else if (conn.state === HubConnectionState.Disconnected) {
          void startWithRetry(true);
        }
      });

      void startWithRetry(false);
    };

    void init();

    //* Browser regained network — skip any pending backoff wait and revive immediately.
    const handleOnline = () => {
      if (disposed || !conn) return;
      noteBrowserEvent('online-revive-nudge', { state: String(conn.state) });
      wakeRetry?.();
      if (conn.state === HubConnectionState.Disconnected) void startWithRetry(true);
    };
    window.addEventListener('online', handleOnline);
    //* אפליקציית המובייל חוזרת מרקע (platform/appLifecycle) — אותה לוגיקה כמו חזרת רשת
    window.addEventListener(SOCKET_RECONNECT_NOW_EVENT, handleOnline);

    //* ===== FIX #1 (2026-07-13, revised 2026-07-14) — Zombie-socket recovery on tab refocus =====
    //* PROBLEM: after long inactivity / a backgrounded tab / laptop sleep, the
    //* browser throttles JS timers, so SignalR's keepAlive + serverTimeout checks
    //* stop running and the underlying WebSocket can die. When that happens the
    //* connection can end up fully closed without the retry loop being awake, so
    //* incoming messages stop arriving until the user manually refreshes the page.
    //*
    //* FIX: watch `visibilitychange`. When the tab becomes visible again:
    //*   (1) wake any pending reconnect backoff so it retries immediately;
    //*   (2) if the socket is fully Disconnected -> revive it now (isRecovery=true
    //*       triggers the standard re-sync so any missed messages are pulled in).
    //*
    //* IMPORTANT — why we DO NOT force a re-sync when state is still "Connected":
    //*   The first revision of this fix also did an HTTP re-sync (getDataOnLoad +
    //*   SOCKET_RECONNECTED_EVENT) on EVERY return to the tab after ~20s, to guard
    //*   against a "zombie" socket (reports Connected but is dead). That caused a
    //*   REGRESSION (2026-07-14): SOCKET_RECONNECTED_EVENT makes the open chat run
    //*   `onUserIdChange(..., forceFresh=true)`, which clears the message list
    //*   (`handleMessagesList([])`) and shows a loading state — i.e. a blank white
    //*   flash + reload on every ordinary tab switch, even when the socket was
    //*   perfectly healthy and had already delivered messages live in the
    //*   background. That block was REMOVED.
    //*
    //*   Rationale: browsers keep WebSockets alive and still deliver *incoming*
    //*   messages to a backgrounded tab (only timers are throttled), so a healthy
    //*   socket keeps the store up to date with no action needed on return. For the
    //*   rarer truly-dead-but-"Connected" case, once the tab is visible again the
    //*   throttled timers resume and SignalR's own serverTimeout detects the dead
    //*   connection within ~30s and reconnects (firing onreconnecting/onclose ->
    //*   our revival), which self-heals WITHOUT a blank flash on every switch.
    //*
    //* SAFETY:
    //*   - Returning to the tab with a healthy socket now does NOTHING visible.
    //*   - Revival only runs when state === Disconnected (a real recovery case),
    //*     mirroring the existing `online` handler.
    //*   - `startWithRetry()` is internally guarded (startLoopRunning) so concurrent
    //*     nudges from the 'online' + visibility handlers cannot spawn duplicate
    //*     start loops.
    //*   - We intentionally do NOT lower serverTimeout/keepAlive, to avoid
    //*     introducing false-positive disconnects on healthy-but-slow connections.
    //*
    //* TO REVERT this fix entirely: delete this whole block AND the matching
    //* `removeEventListener('visibilitychange', ...)` line in the cleanup below.
    //* TO RESTORE the removed forced re-sync (NOT recommended — brings back the
    //* blank-flash regression): re-add a hidden-duration tracker and, in the
    //* visible branch when state is Connected, call `getDataOnLoad()` +
    //* `window.dispatchEvent(new CustomEvent(SOCKET_RECONNECTED_EVENT))`.
    const handleVisibility = () => {
      if (disposed || !conn) return;
      if (document.visibilityState !== 'visible') return;

      //* (1) Skip any pending backoff wait — retry right now.
      wakeRetry?.();

      //* (2) Socket fully closed while hidden -> revive immediately (isRecovery=true re-syncs).
      if (conn.state === HubConnectionState.Disconnected) {
        noteBrowserEvent('visibility-revive', { state: String(conn.state) });
        void startWithRetry(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      probeCleanup?.();
      reconnectorCleanup?.();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(SOCKET_RECONNECT_NOW_EVENT, handleOnline);
      //* ===== FIX #1 (2026-07-13) cleanup — remove the visibility recovery listener. =====
      document.removeEventListener('visibilitychange', handleVisibility);
      if (retryTimer) clearTimeout(retryTimer);
      wakeRetry?.();
      if (conn) {
        conn
          .stop()
          .then(() => {
            isConnectionRef.current = false;
            console.log('Connection stopped');
          })
          .catch((err) => console.error('Error while stopping connection: ', err));
      }
    };
  }, [isLoaded, isSignedIn]);

  //* If user lost socket connection and then reconnected
  //* Do general data requests again to keep data up to date
  useEffect(() => {
    if (connection) {
      connection.onreconnecting((error) => {
        console.log('[DEBUG-REFRESH] SignalR onreconnecting fired', new Date().toISOString(), error);
        socketConnectionHealth.reconnecting(error);
      });
      connection.onclose((error) => {
        console.log('[DEBUG-REFRESH] SignalR onclose fired', new Date().toISOString(), error);
      });
      connection.onreconnected(() => {
        console.log('[DEBUG-REFRESH] SignalR onreconnected fired', new Date().toISOString());
        socketConnectionHealth.reconnected(connection.connectionId ?? null);

        //* Refresh general application data silently in background.
        //* Chat user lists stay as-is — no state reset, no loader flash.
        //* Views will re-fetch fresh data on next navigation.
        getDataOnLoad();
      });
    }
  }, [connection]);

  //* Online / offline indication
  useEffect(() => {
    let offlineNotif: any = null;
    const handleOnline = () => {
      if (offlineNotif !== null) closeSnackbar(offlineNotif);
    };
    const handleOffline = () => {
      offlineNotif = enqueueSnackbar('Connection lost', {
        autoHideDuration: null,
        action: () => {
          return <></>;
        },
        variant: 'warning',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [closeSnackbar, enqueueSnackbar]);

  return <SignalRContext.Provider value={{ connection }}>{children}</SignalRContext.Provider>;
};

export { SignalRContext, SignalRProvider };
